import React, { useState } from "react";
import { useWalletConnect } from "@btc-vision/walletconnect";
import { useCreateAndFundEscrow, useComputeFee } from "../hooks/useEscrow";
import { useEscrowClient } from "../hooks/useEscrowClient";
import { MOTOSETTLE_CONFIG } from "../config";

interface FormState {
  seller:      string;
  arbitrator:  string;
  token:       string;
  amount:      string;
  fundBlocks:  string;
  tradeBlocks: string;
}

const INITIAL: FormState = {
  seller:      "",
  arbitrator:  "",
  token:       "",
  amount:      "1000",
  fundBlocks:  "100",
  tradeBlocks: "500",
};

export function CreateEscrowPage() {
  const { walletAddress } = useWalletConnect();
  const client = useEscrowClient();
  const createAndFund = useCreateAndFundEscrow();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [createdId, setCreatedId] = useState<bigint | null>(null);
  const [diagResult, setDiagResult] = useState("");

  const amountBigInt = (() => {
    try { return BigInt(Math.floor(parseFloat(form.amount) * 1e18)); }
    catch { return 0n; }
  })();

  const fee = useComputeFee(
    form.token || undefined,
    amountBigInt,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    // Deadlines must be absolute block numbers, not relative offsets
    const currentBlock = await client.provider.getBlockNumber();
    const fundDeadline = currentBlock + BigInt(form.fundBlocks);
    const tradeDeadline = fundDeadline + BigInt(form.tradeBlocks);

    createAndFund.mutate(
      {
        seller: form.seller,
        arbitrator: form.arbitrator,
        token: form.token,
        amount: amountBigInt,
        fundDeadline,
        tradeDeadline,
        motoBalance: 0n,
      },
      {
        onSuccess: (escrowId) => {
          setCreatedId(escrowId);
          setForm(INITIAL);
        },
      },
    );
  };

  if (!walletAddress) {
    return (
      <div className="page">
        <div className="empty-state">
          <div className="empty-icon">🔐</div>
          <h3>Wallet not connected</h3>
          <p>Connect your wallet to create a trustless escrow on OP_NET.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Create Escrow</h1>
        <p>Deploy a new trustless escrow — funds are locked on-chain until conditions are met.</p>
      </div>

      {/* Success banner */}
      {createdId !== null && (
        <div className="alert alert-success" data-testid="success-banner" style={{ marginBottom: 24 }}>
          <span>✓</span>
          <span>
            Escrow <strong>#{createdId.toString()}</strong> created and funded!{" "}
            <a href={`/escrow/${createdId}`} style={{ color: "inherit", textDecoration: "underline" }}>
              View details →
            </a>
          </span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 32, alignItems: "start" }}>
        {/* ── Form ── */}
        <div className="card">
          <form onSubmit={handleSubmit} className="form" style={{ maxWidth: "100%" }}>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div className="form-group">
                <label htmlFor="seller">Seller Address</label>
                <input id="seller" name="seller" value={form.seller} onChange={handleChange}
                  placeholder="0x…" required />
              </div>
              <div className="form-group">
                <label htmlFor="arbitrator">Arbitrator Address</label>
                <input id="arbitrator" name="arbitrator" value={form.arbitrator} onChange={handleChange}
                  placeholder="0x…" required />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="token">Token Address (OP-20)</label>
              <input id="token" name="token" value={form.token} onChange={handleChange}
                placeholder="0x…" required />
            </div>

            <div className="form-group">
              <label htmlFor="amount">Amount (in token units)</label>
              <input id="amount" name="amount" type="number" min="0"
                value={form.amount} onChange={handleChange} required />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div className="form-group">
                <label htmlFor="fundBlocks">Fund Deadline (blocks from now)</label>
                <input id="fundBlocks" name="fundBlocks" type="number" min="1"
                  value={form.fundBlocks} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="tradeBlocks">Trade Deadline (blocks after fund)</label>
                <input id="tradeBlocks" name="tradeBlocks" type="number" min="1"
                  value={form.tradeBlocks} onChange={handleChange} required />
              </div>
            </div>

            {/* Fee preview */}
            {fee.data !== undefined && (
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>
                Protocol fee: {fee.data.toString()}
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-lg" disabled={createAndFund.isPending || !client}>
              {createAndFund.isPending ? "Creating & Funding…" : !client ? "Connecting…" : "Create & Fund Escrow"}
            </button>

            {/* Diagnostic: call EscrowCore.createEscrow directly (bypasses Router) */}
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              disabled={!client || !form.seller || !form.arbitrator || !form.token}
              onClick={async () => {
                if (!client) return;
                setDiagResult("Diagnosticando...");
                try {
                  // Step 1: Get current block
                  const currentBlock = await client.provider.getBlockNumber();
                  const fundDl = currentBlock + BigInt(form.fundBlocks);
                  const tradeDl = fundDl + BigInt(form.tradeBlocks);
                  setDiagResult(`Bloque actual: ${currentBlock} | fundDl: ${fundDl} | tradeDl: ${tradeDl}`);

                  // Step 2: Resolve all addresses
                  setDiagResult(prev => prev + "\nResolviendo direcciones...");
                  const [sellerAddr, arbAddr, tokenAddr] = await Promise.all([
                    client.provider.getPublicKeyInfo(form.seller, true),
                    client.provider.getPublicKeyInfo(form.arbitrator, true),
                    client.provider.getPublicKeyInfo(form.token, true),
                  ]);

                  setDiagResult(prev => prev +
                    `\n  seller: ${sellerAddr ? "OK" : "FALLO"}` +
                    `\n  arbitrador: ${arbAddr ? "OK" : "FALLO"}` +
                    `\n  token: ${tokenAddr ? "OK" : "FALLO"}`
                  );

                  if (!sellerAddr || !arbAddr || !tokenAddr) {
                    setDiagResult(prev => prev + "\nALGUNA DIRECCION NO SE RESOLVIO");
                    return;
                  }

                  // Step 3b: Test escrowCount() - simplest view
                  setDiagResult(prev => prev + "\nLlamando EscrowCore.escrowCount()...");
                  try {
                    const countRes = await client.escrowContract.escrowCount();
                    if ("error" in countRes) {
                      setDiagResult(prev => prev + `\n  escrowCount ERROR: ${countRes.error}`);
                    } else {
                      setDiagResult(prev => prev + `\n  escrowCount OK: ${countRes.properties?.count}`);
                    }
                  } catch (e2: any) {
                    setDiagResult(prev => prev + `\n  escrowCount EXCEPTION: ${e2.message}`);
                  }

                  // Step 4: Call EscrowCore.createEscrow directly (bypasses Router)
                  setDiagResult(prev => prev + "\nLlamando EscrowCore.createEscrow...");
                  const sim = await client.escrowContract.createEscrow(
                    sellerAddr,
                    arbAddr,
                    tokenAddr,
                    amountBigInt,
                    fundDl,
                    tradeDl,
                  );

                  if ("error" in sim) {
                    setDiagResult(prev => prev + `\nEscrowCore ERROR: ${sim.error}`);
                  } else if (sim.revert) {
                    setDiagResult(prev => prev + `\nEscrowCore REVERT: ${sim.revert}`);
                  } else {
                    setDiagResult(prev => prev + `\nEscrowCore OK! escrowId=${sim.properties?.escrowId}. El problema esta en el Router, no en EscrowCore.`);
                  }
                } catch (e: any) {
                  setDiagResult(prev => prev + `\nException: ${e.message}`);
                }
              }}
            >
              Diagnosticar (sin Router)
            </button>

            {diagResult && (
              <div className="alert alert-info" style={{ marginTop: 8 }}>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>{diagResult}</pre>
              </div>
            )}

            {createAndFund.error && (
              <div className="alert alert-error">
                <span>✕</span>
                <span>{(createAndFund.error as Error).message}</span>
              </div>
            )}
          </form>
        </div>

        {/* ── Sidebar ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card" style={{ background: "rgba(111,76,255,0.06)", borderColor: "rgba(111,76,255,0.18)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--purple-light)", marginBottom: 12 }}>
              How it works
            </div>
            {[
              ["1", "Buyer creates & funds", "Tokens locked in EscrowCore on-chain."],
              ["2", "Seller activates", "Confirms readiness to deliver goods/service."],
              ["3", "Release or dispute", "Buyer releases, or either party opens a dispute."],
              ["4", "Arbitrator resolves", "If disputed, arbitrator splits funds by allocation."],
            ].map(([num, title, desc]) => (
              <div key={num} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: "var(--purple-dim)", border: "1px solid rgba(111,76,255,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, color: "var(--purple-light)", flexShrink: 0,
                }}>{num}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
